import { Tooltip } from '@mantine/core'

import {
  CarrierBadge,
  CarrierBadgeTone,
  CarrierOptionCompany,
  carrierBadges,
  distinctDbaName,
  formatHeadquarters,
  formatPowerUnits,
} from './carrierOptionDetails'

const BADGE_TONE: Record<CarrierBadgeTone, string> = {
  neutral: 'bg-doubleNickel-gray-100 text-doubleNickel-gray-600',
  danger: 'bg-doubleNickel-error-50 text-doubleNickel-error-700',
}

const StatusBadge = ({ badge }: { badge: CarrierBadge }) => (
  <Tooltip label={badge.tooltip} withArrow position="top">
    <span
      className={`shrink-0 cursor-default whitespace-nowrap rounded px-1 py-px text-[10px] font-medium ${
        BADGE_TONE[badge.tone]
      }`}
    >
      {badge.label}
    </span>
  </Tooltip>
)

/**
 * One carrier search result, without the row it sits in. Left: the legal name
 * with any status badges, the DBA when it adds something, and HQ. Right,
 * aligned down the list: DOT number and fleet size, the two facts that tell
 * one carrier's many registrations apart, so they scan as a column.
 */
const CarrierOptionBody = ({ company }: { company: CarrierOptionCompany }) => {
  const { companyName, dotNumber } = company
  const dba = distinctDbaName(company)
  const badges = carrierBadges(company)
  const headquarters = formatHeadquarters(company)
  const trucks = formatPowerUnits(company.powerUnits)

  return (
    <>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-doubleNickel-gray-800">
            {companyName}
          </span>
          {badges.map((badge) => (
            <StatusBadge key={badge.label} badge={badge} />
          ))}
        </div>
        {dba && (
          <span className="text-[11px] text-doubleNickel-gray-600">
            DBA {dba}
          </span>
        )}
        {headquarters && (
          <span className="text-[11px] text-doubleNickel-gray-500">
            {headquarters}
          </span>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end text-right tabular-nums">
        {dotNumber && (
          <span className="text-[11px] font-medium text-doubleNickel-gray-700">
            DOT {dotNumber}
          </span>
        )}
        {trucks && (
          <span className="text-[11px] text-doubleNickel-gray-500">
            {trucks}
          </span>
        )}
      </div>
    </>
  )
}

export default CarrierOptionBody
